import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button, Input } from '../ui';
import { MetaWebhookHelp } from './MetaWebhookHelp';
import { SectionShell } from './SectionShell';
import type { SectionStatus, TenantSectionProps } from './types';

export interface WhatsAppValue {
  secretsUploaded?: boolean;
  phoneNumberId?: string;
  webhookRegistered?: boolean;
}

type State = 'pending' | 'creds-uploading' | 'creds-uploaded' | 'webhook-registering' | 'done';

export function whatsappStatus(value: WhatsAppValue | undefined): SectionStatus {
  if (value?.webhookRegistered) return 'complete';
  if (value?.secretsUploaded) return 'partial';
  return 'empty';
}

interface Props extends TenantSectionProps<WhatsAppValue> {
  /** Wizard: avanzar de paso. No guarda nada, solo navega. */
  onContinue?: () => void;
}

export function WhatsAppSection({ mode, tenantId, value, onSaved, onContinue, testId }: Props) {
  const queryClient = useQueryClient();
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState(value?.phoneNumberId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [metaInstructions, setMetaInstructions] = useState<string | null>(null);
  const [verifyTokenToCopy, setVerifyTokenToCopy] = useState<string | null>(null);
  const [state, setState] = useState<State>(
    value?.secretsUploaded ? (value.webhookRegistered ? 'done' : 'creds-uploaded') : 'pending',
  );

  const credsSaved =
    state === 'creds-uploaded' || state === 'webhook-registering' || state === 'done';
  const canEditCreds = mode === 'edit' || state === 'pending' || state === 'creds-uploading';

  const webhookBase = import.meta.env.VITE_WEBHOOK_BASE_URL as string | undefined;
  const webhookUrl = webhookBase ? `${webhookBase.replace(/\/+$/, '')}/webhook/whatsapp` : null;

  async function uploadSecrets() {
    if (!appSecret || !accessToken || !phoneNumberId) {
      setError('Todos los campos son requeridos');
      return;
    }
    setError(null);
    setState('creds-uploading');
    try {
      await api.tenants.uploadWhatsAppSecrets(tenantId, { appSecret, accessToken, phoneNumberId });
      onSaved?.({ secretsUploaded: true, phoneNumberId });
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', tenantId] });
      setState('creds-uploaded');
    } catch (err) {
      setError((err as Error).message);
      setState('pending');
    }
  }

  async function connectWebhook() {
    if (!appId) {
      setError('El App ID es requerido para conectar el webhook');
      return;
    }
    setError(null);
    setMetaInstructions(null);
    setVerifyTokenToCopy(null);
    setState('webhook-registering');
    try {
      const res = await api.tenants.setupWhatsAppWebhook(tenantId, { appId, phoneNumberId });
      if (res.ok) {
        onSaved?.({ webhookRegistered: true });
        queryClient.invalidateQueries({ queryKey: ['onboarding-status', tenantId] });
        setState('done');
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setError(errMsg);
      setState('creds-uploaded');
      if (errMsg.includes('verify-token:')) {
        const match = errMsg.match(/verify-token:\s*([a-f0-9]+)/i);
        if (match?.[1]) setVerifyTokenToCopy(match[1]);
      }
      setMetaInstructions(
        'El webhook no se pudo conectar automáticamente en Meta. Por favor, configúralo manualmente en el Meta Dev Portal.',
      );
    }
  }

  // GAP-C: el label decía "Omitir WhatsApp" incluso con los secretos ya
  // guardados, y el operador creía que perdía la configuración.
  const footer =
    mode === 'wizard' ? (
      state === 'done' ? (
        <Button variant="primary" onClick={onContinue}>
          Siguiente →
        </Button>
      ) : credsSaved ? (
        <Button variant="primary" onClick={onContinue}>
          Continuar (webhook manual pendiente) →
        </Button>
      ) : (
        <Button variant="outline" onClick={onContinue}>
          Omitir WhatsApp
        </Button>
      )
    ) : undefined;

  return (
    <SectionShell
      mode={mode}
      testId={testId}
      title="WhatsApp Business"
      description="Credenciales de Meta y registro del webhook por donde llegan los mensajes."
      status={whatsappStatus({ secretsUploaded: credsSaved, webhookRegistered: state === 'done' })}
      error={error}
      footer={footer}
    >
      <section className="space-y-4">
        <h3 className="text-body-md font-medium text-neutral-800 dark:text-neutral-100">
          1. Subir credenciales
        </h3>
        <Input
          label="Phone Number ID"
          id="wa-phone-id"
          value={phoneNumberId}
          onChange={(e) => setPhoneNumberId(e.target.value)}
          placeholder="123456789012345"
          disabled={!canEditCreds}
        />
        <Input
          label="Access Token (permanente)"
          id="wa-token"
          type="password"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          placeholder="EAAB..."
          disabled={!canEditCreds}
        />
        <Input
          label="App Secret"
          id="wa-secret"
          type="password"
          value={appSecret}
          onChange={(e) => setAppSecret(e.target.value)}
          placeholder="App Secret desde Meta Dashboard"
          disabled={!canEditCreds}
        />

        {canEditCreds && (
          <Button
            variant="primary"
            onClick={uploadSecrets}
            loading={state === 'creds-uploading'}
            disabled={state === 'creds-uploading'}
          >
            {state === 'creds-uploading' ? 'Subiendo...' : 'Subir secrets'}
          </Button>
        )}

        {credsSaved && (
          <div>
            <span
              data-testid="step-3-status"
              className="inline-block bg-success-50 text-success-700 p-2 rounded text-sm font-medium"
            >
              ✅ Secrets guardados en AWS
            </span>
          </div>
        )}
      </section>

      {credsSaved && (
        <section className="space-y-4 pt-4 border-t border-neutral-100 dark:border-neutral-700">
          <h3 className="text-body-md font-medium text-neutral-800 dark:text-neutral-100">
            2. Registrar webhook
          </h3>
          <Input
            label="App ID"
            id="wa-app-id"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            placeholder="App ID desde Meta Dashboard"
            disabled={state === 'webhook-registering' || state === 'done'}
          />

          {state === 'done' ? (
            <span className="inline-block bg-success-50 text-success-700 p-2 rounded text-sm font-medium">
              ✅ Webhook conectado exitosamente en Meta
            </span>
          ) : (
            <Button
              variant="primary"
              onClick={connectWebhook}
              disabled={!appId || state === 'webhook-registering'}
              loading={state === 'webhook-registering'}
            >
              {state === 'webhook-registering' ? 'Registrando webhook...' : 'Conectar webhook'}
            </Button>
          )}
        </section>
      )}

      {metaInstructions && (
        <MetaWebhookHelp
          message={metaInstructions}
          appId={appId}
          webhookUrl={webhookUrl}
          verifyToken={verifyTokenToCopy}
        />
      )}
    </SectionShell>
  );
}
